import React, { useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

/**
 * Reusable component for parsing and rendering code blocks with syntax highlighting
 *
 * @param {string} content - The content string that may contain code blocks
 * @param {function} onCodeBlockParsed - Optional callback when code blocks are found
 * @param {object} codeBlockStyle - Optional custom styles for code blocks
 * @param {object} syntaxHighlighterProps - Optional props to pass to SyntaxHighlighter
 * @returns {Array} Array of React elements (HTML content and code blocks)
 */
const SyntaxHighlightedCode = ({
  content,
  onCodeBlockParsed,
  codeBlockStyle = {},
  syntaxHighlighterProps = {},
}) => {
  // Parse code blocks from content string
  const parseCodeBlocks = useCallback((htmlString) => {
    if (!htmlString) return { parts: [], hasCodeBlocks: false };

    // Try multiple regex patterns to match different code block formats
    const patterns = [
      /```(\w+)?\\n([\s\S]*?)\\n```/g, // Escaped newlines: \\n (most common)
      /```(\w+)?\n([\s\S]*?)\n```/g, // Actual newlines: \n
      /&#96;&#96;&#96;(\w+)?\\n([\s\S]*?)\\n&#96;&#96;&#96;/g, // HTML encoded backticks with escaped newlines
      /&#96;&#96;&#96;(\w+)?\n([\s\S]*?)\n&#96;&#96;&#96;/g, // HTML encoded backticks with actual newlines
      /&lt;pre&gt;&lt;code[^&]*?&gt;([\s\S]*?)&lt;\/code&gt;&lt;\/pre&gt;/gi, // HTML encoded
      /<pre><code[^>]*?>([\s\S]*?)<\/code><\/pre>/gi, // HTML tags
      /```\s*(\w+)?\s*\\n([\s\S]*?)\\n\s*```/g, // Flexible with spaces
    ];

    const parts = [];
    let hasCodeBlocks = false;

    // Try each pattern
    for (let patternIndex = 0; patternIndex < patterns.length; patternIndex++) {
      const regex = patterns[patternIndex];
      regex.lastIndex = 0; // Reset regex
      let match;
      const matches = [];
      const tempParts = [];
      let tempLastIndex = 0;

      // Collect all matches
      while ((match = regex.exec(htmlString)) !== null) {
        matches.push({ ...match, patternIndex });
      }

      if (matches.length > 0) {
        hasCodeBlocks = true;

        for (const match of matches) {
          // Add text before code block
          if (match.index > tempLastIndex) {
            const textPart = htmlString.substring(tempLastIndex, match.index);
            if (textPart) {
              tempParts.push({
                type: "html",
                content: textPart,
                index: tempLastIndex,
              });
            }
          }

          let language = "plaintext";
          let code = "";

          if (
            match.patternIndex === 0 ||
            match.patternIndex === 1 ||
            match.patternIndex === 2 ||
            match.patternIndex === 3 ||
            match.patternIndex === 6
          ) {
            // Markdown format: ```language\ncode\n``` (or HTML encoded backticks)
            language = match[1] ? match[1].trim() : "plaintext";
            code = match[2] || "";

            // Unescape newlines for escaped versions
            if (
              match.patternIndex === 0 ||
              match.patternIndex === 2 ||
              match.patternIndex === 6
            ) {
              code = code.replace(/\\n/g, "\n");
            }
          } else {
            // HTML format: <pre><code>code</code></pre>
            code = match[1] || "";
            // Try to extract language from class attribute if present
            const codeTagMatch = match[0].match(/class=["']([^"']*?)["']/i);
            if (codeTagMatch) {
              const classValue = codeTagMatch[1];
              const langMatch = classValue.match(/language-(\w+)/);
              if (langMatch) {
                language = langMatch[1];
              }
            }
          }

          // Comprehensive code cleaning and formatting
          // Step 1: Convert HTML line breaks to actual newlines
          code = code.replace(/<br\s*\/?>/gi, "\n").replace(/<br>/gi, "\n");

          // Step 2: Convert literal escape sequences to actual characters
          const BACKSLASH_PLACEHOLDER = "__TEMP_BACKSLASH__";
          code = code.replace(/\\\\/g, BACKSLASH_PLACEHOLDER);
          code = code
            .replace(/\\n/g, "\n")
            .replace(/\\t/g, "\t")
            .replace(/\\r/g, "\r")
            .replace(/\\'/g, "'")
            .replace(/\\"/g, '"');
          code = code.replace(new RegExp(BACKSLASH_PLACEHOLDER, "g"), "\\");

          // Step 3: Unescape HTML entities
          code = code
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&#x27;/g, "'")
            .replace(/&#x2F;/g, "/")
            .replace(/&#x0A;/g, "\n")
            .replace(/&#x0D;/g, "\r")
            .replace(/&nbsp;/g, " ");

          // Step 4: Decode numeric HTML entities
          code = code.replace(/&#(\d+);/g, (match, dec) => {
            return String.fromCharCode(parseInt(dec, 10));
          });

          // Step 5: Decode hex HTML entities
          code = code.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => {
            return String.fromCharCode(parseInt(hex, 16));
          });

          // Step 6: Normalize line endings
          code = code.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

          // Step 7: Clean up but preserve indentation
          code = code.replace(/^\n+|\n+$/g, "");

          tempParts.push({
            type: "code",
            language: language.toLowerCase(),
            content: code,
            index: match.index,
          });

          tempLastIndex = match.index + match[0].length;
        }

        // Add remaining text after last code block
        if (tempLastIndex < htmlString.length) {
          const textPart = htmlString.substring(tempLastIndex);
          if (textPart) {
            tempParts.push({
              type: "html",
              content: textPart,
              index: tempLastIndex,
            });
          }
        }

        // Sort by index and merge
        tempParts.sort((a, b) => a.index - b.index);
        parts.push(
          ...tempParts.map((p) => {
            const part = { type: p.type, content: p.content };
            if (p.type === "code" && p.language) {
              part.language = p.language;
            }
            return part;
          })
        );
        break; // Use first pattern that finds matches
      }
    }

    // If no code blocks found, return original as single HTML part
    if (!hasCodeBlocks) {
      return {
        parts: [{ type: "html", content: htmlString }],
        hasCodeBlocks: false,
      };
    }

    return { parts, hasCodeBlocks };
  }, []);

  // Parse content
  const parsedContent = useMemo(() => {
    const result = parseCodeBlocks(content);

    // Callback if provided
    if (onCodeBlockParsed && result.hasCodeBlocks) {
      onCodeBlockParsed(result);
    }

    return result;
  }, [content, parseCodeBlocks, onCodeBlockParsed]);

  // Default styles for code blocks
  const defaultCodeBlockStyle = {
    my: 2,
    borderRadius: "4px",
    overflow: "hidden",
    border: "1px solid",
    borderColor: "divider",
    "& pre": {
      margin: 0,
      borderRadius: "4px",
    },
    ...codeBlockStyle,
  };

  // Default SyntaxHighlighter props
  const defaultSyntaxHighlighterProps = {
    style: vscDarkPlus,
    customStyle: {
      margin: 0,
      padding: "16px",
      fontSize: "14px",
      lineHeight: "1.5",
      fontFamily: "'Courier New', 'Monaco', 'Consolas', monospace",
      whiteSpace: "pre",
      overflowX: "auto",
    },
    showLineNumbers: false,
    wrapLines: false,
    wrapLongLines: false,
    PreTag: "div",
    codeTagProps: {
      style: {
        fontFamily: "'Courier New', 'Monaco', 'Consolas', monospace",
        whiteSpace: "pre",
      },
    },
    ...syntaxHighlighterProps,
  };

  // If no code blocks, return the original HTML content
  if (!parsedContent.hasCodeBlocks) {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  // Render mixed content with code blocks
  return parsedContent.parts.map((part, index) => {
    if (part.type === "code") {
      return (
        <Box key={`code-${index}`} sx={defaultCodeBlockStyle}>
          <SyntaxHighlighter
            language={part.language}
            {...defaultSyntaxHighlighterProps}
          >
            {part.content}
          </SyntaxHighlighter>
        </Box>
      );
    } else {
      return (
        <div
          key={`html-${index}`}
          dangerouslySetInnerHTML={{ __html: part.content }}
        />
      );
    }
  });
};

SyntaxHighlightedCode.displayName = "SyntaxHighlightedCode";

export default SyntaxHighlightedCode;
